import React from "react";
import avomaLogo from "../connectors/avoma.png";
import circlebackLogo from "../connectors/circleback.png";
import fathomLogo from "../connectors/fathom.svg";
import fellowLogo from "../connectors/fellow.svg";
import firefliesLogo from "../connectors/fireflies.png";
import gmailLogo from "../connectors/gmail.png";
import gongLogo from "../connectors/gong.png";
import googleCalendarLogo from "../connectors/google-calendar.png";
import googleMeetLogo from "../connectors/google-meet.png";
import grainLogo from "../connectors/grain.png";
import granolaLogo from "../connectors/granola.png";
import hubspotLogo from "../connectors/hubspot.svg";
import microsoftTeamsLogo from "../connectors/microsoftteams.svg";
import notionLogo from "../connectors/notion.svg";
import otterLogo from "../connectors/otter.png";
import readaiLogo from "../connectors/readai.png";
import salesforceLogo from "../connectors/salesforce.svg";
import slackLogo from "../connectors/slack.png";
import tldvLogo from "../connectors/tldv.png";
import whatsappLogo from "../connectors/whatsapp.svg";
import zoomLogo from "../connectors/zoom-brand.svg";

/**
 * Official connector logo sources (bundled as static assets):
 * - Gmail / Google Calendar / Google Meet: gstatic.com product icons
 * - Slack: a.slack-edge.com marketing icon
 * - Zoom, HubSpot, Salesforce, Microsoft Teams, Notion, Fathom, WhatsApp: Simple Icons
 * - Granola, Gong, Otter, Fireflies, tl;dv, Avoma, Read.ai, Grain, Fellow, Circleback: company favicons / touch icons
 */

const createBrandIcon = (src) => {
  const BrandIcon = ({ className = "", alt = "", ...props }) => (
    <img
      src={src}
      alt={alt}
      className={className}
      aria-hidden={!alt}
      draggable={false}
      {...props}
    />
  );
  return BrandIcon;
};

/** @example <GmailBrandIcon className="business-catalog-app-icon__svg" /> */
export const GmailBrandIcon = createBrandIcon(gmailLogo);

/** @example <ZoomBrandIcon className="business-catalog-app-icon__svg" /> */
export const ZoomBrandIcon = createBrandIcon(zoomLogo);

/** @example <GranolaBrandIcon className="business-catalog-app-icon__svg" /> */
export const GranolaBrandIcon = createBrandIcon(granolaLogo);

/** @example <GoogleCalendarBrandIcon className="business-catalog-app-icon__svg" /> */
export const GoogleCalendarBrandIcon = createBrandIcon(googleCalendarLogo);

/** @example <SlackBrandIcon className="business-catalog-app-icon__svg" /> */
export const SlackBrandIcon = createBrandIcon(slackLogo);

/** @example <GongBrandIcon className="business-catalog-app-icon__svg" /> */
export const GongBrandIcon = createBrandIcon(gongLogo);

/** @example <OtterBrandIcon className="business-catalog-app-icon__svg" /> */
export const OtterBrandIcon = createBrandIcon(otterLogo);

/** @example <FirefliesBrandIcon className="business-catalog-app-icon__svg" /> */
export const FirefliesBrandIcon = createBrandIcon(firefliesLogo);

/** @example <FathomBrandIcon className="business-catalog-app-icon__svg" /> */
export const FathomBrandIcon = createBrandIcon(fathomLogo);

/** @example <TldvBrandIcon className="business-catalog-app-icon__svg" /> */
export const TldvBrandIcon = createBrandIcon(tldvLogo);

/** @example <AvomaBrandIcon className="business-catalog-app-icon__svg" /> */
export const AvomaBrandIcon = createBrandIcon(avomaLogo);

/** @example <ReadaiBrandIcon className="business-catalog-app-icon__svg" /> */
export const ReadaiBrandIcon = createBrandIcon(readaiLogo);

/** @example <GrainBrandIcon className="business-catalog-app-icon__svg" /> */
export const GrainBrandIcon = createBrandIcon(grainLogo);

/** @example <CirclebackBrandIcon className="business-catalog-app-icon__svg" /> */
export const CirclebackBrandIcon = createBrandIcon(circlebackLogo);

/** @example <FellowBrandIcon className="business-catalog-app-icon__svg" /> */
export const FellowBrandIcon = createBrandIcon(fellowLogo);

/** @example <GoogleMeetBrandIcon className="business-catalog-app-icon__svg" /> */
export const GoogleMeetBrandIcon = createBrandIcon(googleMeetLogo);

/** @example <MicrosoftTeamsBrandIcon className="business-catalog-app-icon__svg" /> */
export const MicrosoftTeamsBrandIcon = createBrandIcon(microsoftTeamsLogo);

/** @example <SalesforceBrandIcon className="business-catalog-app-icon__svg" /> */
export const SalesforceBrandIcon = createBrandIcon(salesforceLogo);

/** @example <HubspotBrandIcon className="business-catalog-app-icon__svg" /> */
export const HubspotBrandIcon = createBrandIcon(hubspotLogo);

/** @example <NotionBrandIcon className="business-catalog-app-icon__svg" /> */
export const NotionBrandIcon = createBrandIcon(notionLogo);

/** @example <WhatsappBrandIcon className="business-catalog-app-icon__svg" /> */
export const WhatsappBrandIcon = createBrandIcon(whatsappLogo);

/**
 * Attio brand mark rendered as inline SVG (no bundled asset), so the CRM
 * connector shows a consistent icon without shipping a new logo file.
 *
 * @example <AttioBrandIcon className="business-catalog-app-icon__svg" />
 */
export const AttioBrandIcon = ({ className = "", alt = "", ...props }) => (
  <svg
    className={className}
    viewBox="0 0 32 32"
    role="img"
    aria-label={alt || undefined}
    aria-hidden={!alt}
    {...props}
  >
    <rect width="32" height="32" rx="7" fill="#1a1a1a" />
    <path
      d="M16 7l6.9 12.4a1 1 0 01-.87 1.48H9.97a1 1 0 01-.87-1.48L16 7z"
      fill="#fff"
    />
    <circle cx="16" cy="21.5" r="2.2" fill="#1a1a1a" />
  </svg>
);

export const CONNECTOR_SOURCE_TYPES = [
  "gmail",
  "zoom",
  "gong",
  "granola",
  "google_calendar",
  "slack",
  "otter",
  "fireflies",
  "fathom",
  "tldv",
  "avoma",
  "readai",
  "grain",
  "circleback",
  "fellow",
  "google_meet",
  "teams",
  "salesforce",
  "hubspot",
  "notion",
  "attio",
  "whatsapp",
];

export const CONNECTOR_ICON_CLASS_BY_SOURCE = {
  gmail: "business-catalog-app-icon--gmail",
  zoom: "business-catalog-app-icon--zoom",
  gong: "business-catalog-app-icon--gong",
  granola: "business-catalog-app-icon--granola",
  google_calendar: "business-catalog-app-icon--google-calendar",
  slack: "business-catalog-app-icon--slack",
  otter: "business-catalog-app-icon--otter",
  fireflies: "business-catalog-app-icon--fireflies",
  fathom: "business-catalog-app-icon--fathom",
  tldv: "business-catalog-app-icon--tldv",
  avoma: "business-catalog-app-icon--avoma",
  readai: "business-catalog-app-icon--readai",
  grain: "business-catalog-app-icon--grain",
  circleback: "business-catalog-app-icon--circleback",
  fellow: "business-catalog-app-icon--fellow",
  google_meet: "business-catalog-app-icon--google-meet",
  teams: "business-catalog-app-icon--teams",
  salesforce: "business-catalog-app-icon--salesforce",
  hubspot: "business-catalog-app-icon--hubspot",
  notion: "business-catalog-app-icon--notion",
  attio: "business-catalog-app-icon--attio",
  whatsapp: "business-catalog-app-icon--whatsapp",
};

export const CONNECTOR_BRAND_ICON_BY_SOURCE = {
  gmail: GmailBrandIcon,
  zoom: ZoomBrandIcon,
  gong: GongBrandIcon,
  granola: GranolaBrandIcon,
  google_calendar: GoogleCalendarBrandIcon,
  slack: SlackBrandIcon,
  otter: OtterBrandIcon,
  fireflies: FirefliesBrandIcon,
  fathom: FathomBrandIcon,
  tldv: TldvBrandIcon,
  avoma: AvomaBrandIcon,
  readai: ReadaiBrandIcon,
  grain: GrainBrandIcon,
  circleback: CirclebackBrandIcon,
  fellow: FellowBrandIcon,
  google_meet: GoogleMeetBrandIcon,
  teams: MicrosoftTeamsBrandIcon,
  salesforce: SalesforceBrandIcon,
  hubspot: HubspotBrandIcon,
  notion: NotionBrandIcon,
  attio: AttioBrandIcon,
  whatsapp: WhatsappBrandIcon,
};

const WORKFLOW_TYPE_TO_SOURCE = {
  email_daily_digest: "gmail",
  zoom_daily_digest: "zoom",
  gong_daily_digest: "gong",
  granola_daily_digest: "granola",
  notion_daily_digest: "notion",
  google_calendar_daily_digest: "google_calendar",
  google_meet_daily_digest: "google_meet",
  slack_daily_digest: "slack",
};

const CONNECTOR_PANEL_ICON_CLASS =
  "business-catalog-app-icon business-simple-connect__icon";
const CONNECTOR_HEADER_ICON_CLASS =
  "business-catalog-app-icon business-wizard-header__icon";

/**
 * Resolve a connector source key from a workflow type or source key.
 *
 * @example
 * resolveConnectorSourceType("zoom_daily_digest");
 * // "zoom"
 */
export const resolveConnectorSourceType = (workflowTypeOrSource) => {
  const key = String(workflowTypeOrSource || "").trim();
  return WORKFLOW_TYPE_TO_SOURCE[key] || key;
};

/**
 * CSS class for a connector brand icon container.
 *
 * @example
 * getConnectorIconClass("zoom");
 * // "business-catalog-app-icon--zoom"
 */
export const getConnectorIconClass = (workflowTypeOrSource) =>
  CONNECTOR_ICON_CLASS_BY_SOURCE[resolveConnectorSourceType(workflowTypeOrSource)] || "";

/**
 * Render a connector company's brand icon.
 *
 * @example
 * renderConnectorBrandIcon("zoom");
 * // <span class="business-catalog-app-icon business-catalog-app-icon--zoom"><img ... /></span>
 *
 * @example
 * renderConnectorBrandIcon("zoom_daily_digest", { placement: "header" });
 * // smaller icon for wizard / settings header
 */
export const renderConnectorBrandIcon = (
  workflowTypeOrSource,
  { placement = "panel", className = "" } = {}
) => {
  const sourceType = resolveConnectorSourceType(workflowTypeOrSource);
  const Icon = CONNECTOR_BRAND_ICON_BY_SOURCE[sourceType];
  if (!Icon) return null;

  const placementClass =
    placement === "header" ? CONNECTOR_HEADER_ICON_CLASS : CONNECTOR_PANEL_ICON_CLASS;
  const iconClass = CONNECTOR_ICON_CLASS_BY_SOURCE[sourceType] || "";
  const containerClass = [placementClass, iconClass, className].filter(Boolean).join(" ");

  return (
    <span className={containerClass}>
      <Icon className="business-catalog-app-icon__svg" />
    </span>
  );
};
